(function () {
  'use strict';

  const memoryOrders = new Map();

  function shuffle(items, random) {
    const ordered = items.slice();
    for (let i = ordered.length - 1; i > 0; i -= 1) {
      const j = Math.floor(random() * (i + 1));
      [ordered[i], ordered[j]] = [ordered[j], ordered[i]];
    }
    return ordered;
  }

  function selectStories(library, productId, category, random = Math.random) {
    const id = String(productId).split('/').pop();
    const tiers = [[], [], []];
    library.forEach(story => {
      const rank = String(story.product_id) === id ? 0 : category && story.category === category ? 1 : 2;
      tiers[rank].push(story);
    });
    const seenIds = new Set();
    const seenSources = new Set();
    const unique = tiers.map(tier => tier.filter(story => {
      const src = String(story.src || '').split(/[?#]/)[0];
      if (!src || seenIds.has(story.id) || seenSources.has(src)) return false;
      seenIds.add(story.id);
      seenSources.add(src);
      return true;
    }));
    const firstTier = unique.findIndex(tier => tier.length);
    return {
      tier: firstTier < 0 ? 'none' : ['exact', 'category', 'random'][firstTier],
      stories: unique.flatMap(tier => shuffle(tier, random)),
    };
  }

  function sessionOrder(productId, ids, storage, random = Math.random) {
    const key = 'tenzen.angels.review-order.v1:' + productId;
    let saved = memoryOrders.get(key);
    if (storage) {
      try {
        saved = JSON.parse(storage.getItem(key));
      } catch {
        // Blocked storage keeps the current page's order; corrupt JSON is replaced.
      }
    }
    const current = new Set(ids);
    if (Array.isArray(saved) && saved.length === ids.length &&
        new Set(saved).size === ids.length && saved.every(id => current.has(id))) return saved;
    const ordered = shuffle(ids, random);
    memoryOrders.set(key, ordered);
    if (storage) {
      try {
        storage.setItem(key, JSON.stringify(ordered));
      } catch {
        // The in-memory order remains usable when the browser blocks storage.
      }
    }
    return ordered;
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = { selectStories, sessionOrder };
  }
  if (typeof document === 'undefined') return;

  function orderReviews(root) {
    const list = root.querySelector('[data-angels-review-list]');
    if (!list) return;
    const cards = new Map(Array.from(list.querySelectorAll('[data-review-id]'))
      .map(card => [card.dataset.reviewId, card]));
    let storage = null;
    try {
      storage = window.sessionStorage;
    } catch {
      // Private or restricted browsing can deny access to sessionStorage itself.
    }
    const ordered = sessionOrder(root.dataset.productId, Array.from(cards.keys()), storage);
    const fragment = document.createDocumentFragment();
    ordered.forEach(id => fragment.append(cards.get(id)));
    list.append(fragment);
    list.scrollLeft = 0;
    requestAnimationFrame(() => { list.scrollLeft = 0; });
  }

  // The story strip. Cards never move in the page: each slides on its own
  // transform and wraps to the back once it has left on the left. The CSS
  // animation plus moving the first card to the end on `animationiteration`
  // (the old way) let the row snap back a card before the move landed, and
  // Safari repainted a frame late, so the row jumped every step (Aditya,
  // 2026-10-14: "the images are kind of flickering"). Same speed as the CSS
  // (--story-step per --story-duration, 50 px/s); never clones a picture.
  function runStrip(root, track) {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const cards = Array.from(track.children);
    if (cards.length < 2 || reduced.matches) return;
    track.style.animation = 'none';
    track.style.willChange = 'auto';
    cards.forEach(card => { card.style.willChange = 'transform'; });
    let step = 0;
    let total = 0;
    let speed = 0;
    let offset = 0;
    let last = 0;
    let raf = 0;
    const at = i => {
      let x = i * step - offset;
      if (x < -step) x += total;
      return x;
    };
    const place = () => {
      for (let i = 0; i < cards.length; i += 1) {
        cards[i].style.transform = 'translate3d(' + (at(i) - i * step).toFixed(2) + 'px,0,0)';
      }
    };
    const measure = () => {
      const progress = total ? offset / total : 0;
      const css = getComputedStyle(root);
      step = cards[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0);
      total = step * cards.length;
      const duration = parseFloat(css.getPropertyValue('--story-duration')) || 2.48;
      speed = (parseFloat(css.getPropertyValue('--story-step')) || step) / duration;
      offset = progress * total;
      if (step) place();
    };
    const frame = now => {
      if (!track.isConnected) return;
      const elapsed = last ? Math.min((now - last) / 1000, 0.1) : 0;
      last = now;
      if (root.dataset.storiesVisible !== '0' && !document.hidden && total) {
        offset = (offset + speed * elapsed) % total;
        place();
      }
      raf = requestAnimationFrame(frame);
    };
    measure();
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(track.parentElement);
    raf = requestAnimationFrame(frame);
    reduced.addEventListener('change', () => {
      if (!reduced.matches) return;
      cancelAnimationFrame(raf);
      cards.forEach(card => { card.style.transform = ''; });
    });
  }

  async function loadStories(root) {
    const track = root.querySelector('[data-angels-story-list]');
    if (!track || !root.dataset.storyLibraryUrl) return;
    const libraryUrl = new URL(root.dataset.storyLibraryUrl, window.location.href);
    const response = await fetch(libraryUrl);
    if (!response.ok) throw new Error('Story library HTTP ' + response.status);
    const library = await response.json();
    const selected = selectStories(library, root.dataset.productId, root.dataset.productCategory);
    root.dataset.storyTier = selected.tier;
    track.setAttribute('aria-hidden', 'true');
    if (!selected.stories.length) {
      track.hidden = true;
      return;
    }
    if ('IntersectionObserver' in window) {
      await new Promise(resolve => {
        const observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          observer.disconnect();
          resolve();
        }, { rootMargin: '400px' });
        observer.observe(track.parentElement);
      });
    }
    const fragment = document.createDocumentFragment();
    const initialImages = [];
    selected.stories.forEach((story, index) => {
      const card = document.createElement('div');
      card.className = 'pdp-angels__story';
      card.dataset.storyId = story.id;
      card.dataset.sourceProductId = story.product_id;
      card.dataset.sourceCategory = story.category;
      card.dataset.storyTier = String(story.product_id) === root.dataset.productId ? 'exact'
        : story.category === root.dataset.productCategory ? 'category' : 'random';
      const image = document.createElement('img');
      image.src = new URL(story.src, libraryUrl).href;
      image.alt = '';
      image.width = story.width;
      image.height = story.height;
      image.loading = index < 18 ? 'eager' : 'lazy';
      image.fetchPriority = 'low';
      image.decoding = 'async';
      if (index < 18) initialImages.push(image);
      card.append(image);
      fragment.append(card);
    });
    track.replaceChildren(fragment);
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        root.dataset.storiesVisible = entries.some(entry => entry.isIntersecting) ? '1' : '0';
      });
      observer.observe(track.parentElement);
    }
    await Promise.allSettled(initialImages.map(image => image.decode()));
    root.dataset.storiesReady = '1';
    runStrip(root, track);
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-angels-reviews]').forEach(root => {
      if (root.dataset.angelsReady === '1') return;
      root.dataset.angelsReady = '1';
      orderReviews(root);
      loadStories(root).catch(error => {
        root.dataset.storiesReady = 'error';
        console.error('Unable to load Tenzen stories.', error);
      });
    });
  }

  document.addEventListener('click', event => {
    const trigger = event.target.closest && event.target.closest('[data-crev-jump]');
    const target = document.getElementById('community-reviews');
    if (!trigger || !target || !target.matches('[data-angels-reviews]')) return;
    event.preventDefault();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  });
  document.addEventListener('shopify:section:load', event => init(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
